import {useDeploymentsQuery} from './data/route-queries'
import {ActionList, RelativeTime} from '@primer/react'
import {DeploymentStatusState, type Deployment} from '@github-ui/dotcom-schema/types/pull_requests/v1/deployments'
import {CheckSpinner} from '@github-ui/mergebox'
import {StatusIcon} from '@github-ui/status-avatar/StatusIcon'
import {STATUS_CHECK_CONFIGS} from '@github-ui/mergebox/helpers/status-check-helpers'
import {SidebarSection} from './SidebarSection'
import sidebarListStyles from './SidebarActionList.module.css'
import {CheckCircleFillIcon, LinkExternalIcon, WorkflowIcon} from '@primer/octicons-react'
import {useOverviewRouteData} from './data/use-overview-route-data'
import {UserSectionSkeleton} from './UserSectionSkeleton'

/**
 * Renders the Deployments sidebar section, including its `SidebarSection` wrapper.
 * The wrapper is owned here (rather than in `OverviewInfoContent`) so the section
 * header is hidden when there are no deployments to show.
 */
export function Deployments() {
  const {repoHasDeployments} = useOverviewRouteData()

  if (!repoHasDeployments) return null

  return (
    <SidebarSection title="Deployments">
      <DeploymentsContent />
    </SidebarSection>
  )
}

const DEPLOYMENTS_STATUS_CONFIGS = {
  [DeploymentStatusState.SUCCESS]: STATUS_CHECK_CONFIGS.SUCCESS,
  [DeploymentStatusState.ACTIVE]: {
    icon: CheckCircleFillIcon,
    iconColor: 'var(--fgColor-success)',
  },
  [DeploymentStatusState.FAILURE]: STATUS_CHECK_CONFIGS.FAILURE,
  [DeploymentStatusState.IN_PROGRESS]: STATUS_CHECK_CONFIGS.IN_PROGRESS,
  [DeploymentStatusState.QUEUED]: STATUS_CHECK_CONFIGS.QUEUED,
  [DeploymentStatusState.PENDING]: STATUS_CHECK_CONFIGS.PENDING,
  [DeploymentStatusState.ERROR]: STATUS_CHECK_CONFIGS.ERROR,
  [DeploymentStatusState.INACTIVE]: {
    icon: CheckCircleFillIcon,
    iconColor: 'var(--fgColor-muted)',
  },
  [DeploymentStatusState.DESTROYED]: STATUS_CHECK_CONFIGS.NEUTRAL,
  [DeploymentStatusState.WAITING]: STATUS_CHECK_CONFIGS.PENDING,
  [DeploymentStatusState.ABANDONED]: STATUS_CHECK_CONFIGS.FAILURE,
  [DeploymentStatusState.UNSPECIFIED]: STATUS_CHECK_CONFIGS._UNKNOWN_VALUE,
} as const

const DEPLOYMENT_STATUS_TEXT = {
  [DeploymentStatusState.SUCCESS]: 'successful',
  [DeploymentStatusState.ACTIVE]: 'active',
  [DeploymentStatusState.FAILURE]: 'failed',
  [DeploymentStatusState.IN_PROGRESS]: 'in progress',
  [DeploymentStatusState.QUEUED]: 'queued',
  [DeploymentStatusState.PENDING]: 'pending',
  [DeploymentStatusState.ERROR]: 'errored',
  [DeploymentStatusState.INACTIVE]: 'inactive',
  [DeploymentStatusState.DESTROYED]: 'destroyed',
  [DeploymentStatusState.WAITING]: 'waiting',
  [DeploymentStatusState.ABANDONED]: 'abandoned',
  [DeploymentStatusState.UNSPECIFIED]: 'unknown',
} as const

function DeploymentItem({deployment}: {deployment: Deployment}) {
  const statusState = DEPLOYMENTS_STATUS_CONFIGS[deployment.status] ?? STATUS_CHECK_CONFIGS._UNKNOWN_VALUE
  const statusText =
    DEPLOYMENT_STATUS_TEXT[deployment.status] ?? DEPLOYMENT_STATUS_TEXT[DeploymentStatusState.UNSPECIFIED]

  const isActive = deployment.status === DeploymentStatusState.ACTIVE
  const isInactive = deployment.status === DeploymentStatusState.INACTIVE

  const statusVisual =
    deployment.status === DeploymentStatusState.IN_PROGRESS ? (
      <span aria-hidden="true">
        <CheckSpinner animated />
      </span>
    ) : (
      <StatusIcon size={16} icon={statusState.icon} iconColor={statusState.iconColor} absolute={false} />
    )
  const timestamp = <RelativeTime datetime={deployment.timestamp} noTitle prefix="" />

  return isInactive ? (
    <ActionList.Item disabled>
      <ActionList.LeadingVisual>{statusVisual}</ActionList.LeadingVisual>
      {deployment.name}
      <ActionList.Description variant="block">inactive</ActionList.Description>
      <ActionList.TrailingVisual>
        <LinkExternalIcon />
      </ActionList.TrailingVisual>
      <ActionList.TrailingAction
        as="a"
        href={deployment.workflowUrl}
        target="_blank"
        rel="noopener noreferrer"
        icon={WorkflowIcon}
        label="View workflow"
        className={sidebarListStyles.topAlignedTrailingAction}
      />
    </ActionList.Item>
  ) : (
    <ActionList.LinkItem href={isActive ? deployment.deploymentUrl : deployment.workflowUrl}>
      <ActionList.LeadingVisual>{statusVisual}</ActionList.LeadingVisual>
      {deployment.name}
      <ActionList.Description variant="block">
        <span className="sr-only">{statusText}, </span>
        {timestamp}
      </ActionList.Description>
      {isActive && (
        <ActionList.TrailingVisual>
          <LinkExternalIcon />
        </ActionList.TrailingVisual>
      )}
      {isActive && (
        <ActionList.TrailingAction
          as="a"
          href={deployment.workflowUrl}
          target="_blank"
          rel="noopener noreferrer"
          icon={WorkflowIcon}
          label="View workflow"
          className={sidebarListStyles.topAlignedTrailingAction}
        />
      )}
    </ActionList.LinkItem>
  )
}

function DeploymentsContent() {
  const {data, isPending, isError} = useDeploymentsQuery()

  if (isPending && !data) return <UserSectionSkeleton placeholderRows={1} />

  if (isError && !data) return <span className={sidebarListStyles.emptyState}>Unable to load deployments</span>

  const deployments = data?.deployments || []

  return deployments.length > 0 ? (
    <ActionList variant="full" role="list" className={sidebarListStyles.flush}>
      {deployments.map(deployment => (
        <DeploymentItem key={`${deployment.commitOid}-${deployment.name}`} deployment={deployment} />
      ))}
    </ActionList>
  ) : (
    <span className={sidebarListStyles.emptyState}>No deployments yet</span>
  )
}
